import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Building2, DollarSign, Smartphone, Trash2, Plus, CheckCircle, X, AlertCircle } from 'lucide-react';
import paymentMethodsApi, { StoredPaymentMethod } from '../../api/paymentMethods';
import PageHeader from '../../components/PageHeader';

type PaymentMethodType = 'BANK_ACCOUNT' | 'BPAY' | 'APPLE_WALLET' | 'GOOGLE_WALLET' | 'CARD';

export default function PaymentMethods() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMethods, setSavedMethods] = useState<StoredPaymentMethod[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedType, setSelectedType] = useState<PaymentMethodType | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form states for different payment types
  const [bankForm, setBankForm] = useState({ bsb: '', accountNumber: '', accountName: '', nickname: '' });
  const [bpayForm, setBpayForm] = useState({ billerCode: '', referenceNumber: '', nickname: '' });
  const [walletForm, setWalletForm] = useState({ nickname: '' });

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    setLoading(true);
    try {
      const methods = await paymentMethodsApi.list();
      setSavedMethods(methods);
    } catch (err) {
      console.error('Failed to load payment methods:', err);
      setError('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMethod = async () => {
    setError('');
    setSaving(true);

    try {
      // Validate based on type
      if (selectedType === 'BANK_ACCOUNT') {
        if (!bankForm.bsb || !bankForm.accountNumber || !bankForm.accountName) {
          setError('Please fill in all required fields');
          setSaving(false);
          return;
        }
        await paymentMethodsApi.addBankAccount({
          label: bankForm.nickname || `Bank Account (${bankForm.bsb})`,
          token: bankForm.accountNumber,
          bsb: bankForm.bsb,
          accountName: bankForm.accountName
        });
      } else if (selectedType === 'BPAY') {
        if (!bpayForm.billerCode || !bpayForm.referenceNumber) {
          setError('Please fill in all required fields');
          setSaving(false);
          return;
        }
        await paymentMethodsApi.addBPay({
          label: bpayForm.nickname || `BPay (${bpayForm.billerCode})`,
          reference: bpayForm.referenceNumber,
          billerCode: bpayForm.billerCode
        });
      } else if (selectedType === 'APPLE_WALLET') {
        if (!walletForm.nickname) {
          setError('Please enter a nickname');
          setSaving(false);
          return;
        }
        await paymentMethodsApi.addAppleWallet({
          label: walletForm.nickname,
          walletTokenId: 'apple-' + Date.now(), // In production, this comes from Apple Pay
          provider: 'APPLE'
        });
      } else if (selectedType === 'GOOGLE_WALLET') {
        if (!walletForm.nickname) {
          setError('Please enter a nickname');
          setSaving(false);
          return;
        }
        await paymentMethodsApi.addGoogleWallet({
          label: walletForm.nickname,
          walletTokenId: 'google-' + Date.now(), // In production, this comes from Google Pay
          provider: 'GOOGLE'
        });
      }

      setSuccess('Payment method added successfully');
      setShowAddModal(false);
      setSelectedType(null);

      // Reset forms
      setBankForm({ bsb: '', accountNumber: '', accountName: '', nickname: '' });
      setBpayForm({ billerCode: '', referenceNumber: '', nickname: '' });
      setWalletForm({ nickname: '' });

      // Reload payment methods
      await loadPaymentMethods();
    } catch (err) {
      console.error('Failed to add payment method:', err);
      setError('Failed to add payment method. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMethod = async (id: string) => {
    if (!confirm('Are you sure you want to remove this payment method?')) return;

    try {
      await paymentMethodsApi.delete(id);
      setSavedMethods(prev => prev.filter(m => m.id !== id));
      setSuccess('Payment method removed');
    } catch (err) {
      console.error('Failed to remove payment method:', err);
      setError('Failed to remove payment method');
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await paymentMethodsApi.setDefault(id);
      setSavedMethods(prev => prev.map(m => ({
        ...m,
        isDefault: m.id === id
      })));
      setSuccess('Default payment method updated');
    } catch (err) {
      console.error('Failed to update default method:', err);
      setError('Failed to update default method');
    }
  };

  const getMethodIcon = (type: string) => {
    switch (type) {
      case 'BANK_ACCOUNT': return <Building2 className="w-6 h-6" />;
      case 'BPAY': return <DollarSign className="w-6 h-6" />;
      case 'APPLE_WALLET':
      case 'GOOGLE_WALLET': return <Smartphone className="w-6 h-6" />;
      default: return <CreditCard className="w-6 h-6" />;
    }
  };

  const getMethodLabel = (type: string) => {
    switch (type) {
      case 'BANK_ACCOUNT': return 'Bank Account';
      case 'BPAY': return 'BPay';
      case 'APPLE_WALLET': return 'Apple Wallet';
      case 'GOOGLE_WALLET': return 'Google Wallet';
      default: return 'Payment Method';
    }
  };

  const getMethodDetails = (method: StoredPaymentMethod) => {
    switch (method.type) {
      case 'BANK_ACCOUNT':
        return `BSB: ${method.bsbOrRouting || '***'} | Account: ${method.accountNumberMasked || '****'}`;
      case 'BPAY':
        return `Biller: ${method.bpayBillerCode || '***'} | Ref: ${method.bpayReference || '****'}`;
      case 'APPLE_WALLET':
      case 'GOOGLE_WALLET':
        return method.walletProvider ? `${method.walletProvider} Wallet` : 'Digital Wallet';
      default:
        return method.label || 'Payment Method';
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <PageHeader
        title="Payment Methods"
        subtitle="Manage your saved payment methods for rent payments"
        icon={<CreditCard className="w-8 h-8" />}
        backPath="/dashboard"
      />

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm text-green-800">{success}</div>
          </div>
          <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm text-red-800">{error}</div>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Saved Payment Methods */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-primary">Saved Methods</h2>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Method
              </button>
            </div>

            {savedMethods.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No payment methods yet</h3>
                <p className="text-gray-600 mb-6">Add a payment method to make rent payments easier</p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="px-6 py-3 bg-secondary text-white rounded-lg hover:bg-secondary/90 transition-colors inline-flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Payment Method
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {savedMethods.map((method) => (
                  <div
                    key={method.id}
                    className={`border-2 rounded-lg p-4 transition-all ${
                      method.isDefault
                        ? 'border-secondary bg-secondary/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600 flex-shrink-0">
                        {getMethodIcon(method.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-1">
                          <div>
                            <h3 className="font-semibold text-gray-900">{method.label}</h3>
                            <p className="text-sm text-gray-600">{getMethodLabel(method.type)}</p>
                          </div>
                          {method.isDefault && (
                            <span className="px-2 py-1 bg-secondary text-white text-xs font-medium rounded">
                              Default
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mb-3">{getMethodDetails(method)}</p>
                        <div className="flex gap-2">
                          {!method.isDefault && (
                            <button
                              onClick={() => handleSetDefault(method.id)}
                              className="text-sm text-secondary hover:text-primary font-medium transition-colors"
                            >
                              Set as Default
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteMethod(method.id)}
                            className="text-sm text-red-600 hover:text-red-800 font-medium transition-colors flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" />
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* One-off Card Payment Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <CreditCard className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Credit/Debit Card Payments</h3>
                <p className="text-sm text-blue-800 mb-3">
                  For security, we don't store credit or debit card details. You can make one-off card payments
                  when paying your rent by selecting "Pay with Card" at checkout.
                </p>
                <button
                  onClick={() => navigate('/renter/pay-rent')}
                  className="text-sm text-blue-700 hover:text-blue-900 font-medium"
                >
                  Go to Pay Rent →
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Sidebar - Info */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="font-semibold text-primary mb-4">Supported Methods</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-sm text-gray-900">Bank Transfer</div>
                  <div className="text-xs text-gray-600">Direct bank account payments via BSB & Account</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <DollarSign className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-sm text-gray-900">BPay</div>
                  <div className="text-xs text-gray-600">Pay using your bank's BPay service</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Smartphone className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-sm text-gray-900">Digital Wallets</div>
                  <div className="text-xs text-gray-600">Apple Pay and Google Pay supported</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CreditCard className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-sm text-gray-900">Card Payments</div>
                  <div className="text-xs text-gray-600">One-off payments only (not stored)</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-secondary/10 to-primary/10 rounded-lg p-6">
            <h3 className="font-semibold text-primary mb-2">Security First</h3>
            <p className="text-sm text-gray-700">
              All payment methods are encrypted and securely stored. We never see your full bank details.
            </p>
          </div>
        </div>
      </div>

      {/* Add Payment Method Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-primary">Add Payment Method</h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedType(null);
                    setError('');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {!selectedType ? (
                // Select Payment Type
                <div className="space-y-3">
                  <p className="text-gray-600 mb-4">Select a payment method type:</p>

                  <button
                    onClick={() => setSelectedType('BANK_ACCOUNT')}
                    className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-secondary hover:bg-secondary/5 transition-all text-left"
                  >
                    <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Bank Account (BSB/Account)</div>
                      <div className="text-sm text-gray-600">Direct bank transfer</div>
                    </div>
                  </button>

                  <button
                    onClick={() => setSelectedType('BPAY')}
                    className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-secondary hover:bg-secondary/5 transition-all text-left"
                  >
                    <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">BPay</div>
                      <div className="text-sm text-gray-600">Pay via your bank's BPay service</div>
                    </div>
                  </button>

                  <button
                    onClick={() => setSelectedType('APPLE_WALLET')}
                    className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-secondary hover:bg-secondary/5 transition-all text-left"
                  >
                    <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
                      <Smartphone className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Apple Wallet</div>
                      <div className="text-sm text-gray-600">Pay with Apple Pay</div>
                    </div>
                  </button>

                  <button
                    onClick={() => setSelectedType('GOOGLE_WALLET')}
                    className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-secondary hover:bg-secondary/5 transition-all text-left"
                  >
                    <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
                      <Smartphone className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Google Wallet</div>
                      <div className="text-sm text-gray-600">Pay with Google Pay</div>
                    </div>
                  </button>
                </div>
              ) : (
                // Payment Method Form
                <div className="space-y-6">
                  <button
                    onClick={() => setSelectedType(null)}
                    className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                  >
                    ← Back to payment types
                  </button>

                  {selectedType === 'BANK_ACCOUNT' && (
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg text-gray-900">Bank Account Details</h3>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          BSB Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="000-000"
                          value={bankForm.bsb}
                          onChange={(e) => setBankForm({ ...bankForm, bsb: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                          maxLength={7}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Account Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="12345678"
                          value={bankForm.accountNumber}
                          onChange={(e) => setBankForm({ ...bankForm, accountNumber: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Account Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="John Doe"
                          value={bankForm.accountName}
                          onChange={(e) => setBankForm({ ...bankForm, accountName: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nickname (Optional)
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., My Commonwealth Account"
                          value={bankForm.nickname}
                          onChange={(e) => setBankForm({ ...bankForm, nickname: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}

                  {selectedType === 'BPAY' && (
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg text-gray-900">BPay Details</h3>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Biller Code <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="123456"
                          value={bpayForm.billerCode}
                          onChange={(e) => setBpayForm({ ...bpayForm, billerCode: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Reference Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="Your reference number"
                          value={bpayForm.referenceNumber}
                          onChange={(e) => setBpayForm({ ...bpayForm, referenceNumber: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nickname (Optional)
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., Rentry BPay"
                          value={bpayForm.nickname}
                          onChange={(e) => setBpayForm({ ...bpayForm, nickname: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}

                  {(selectedType === 'APPLE_WALLET' || selectedType === 'GOOGLE_WALLET') && (
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg text-gray-900">
                        {selectedType === 'APPLE_WALLET' ? 'Apple Wallet' : 'Google Wallet'}
                      </h3>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <p className="text-sm text-blue-800">
                          You'll be prompted to authenticate with {selectedType === 'APPLE_WALLET' ? 'Apple Pay' : 'Google Pay'}
                          when making a payment. Your wallet details are never stored on our servers.
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nickname <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder={`e.g., My ${selectedType === 'APPLE_WALLET' ? 'Apple' : 'Google'} Wallet`}
                          value={walletForm.nickname}
                          onChange={(e) => setWalletForm({ ...walletForm, nickname: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-red-800">{error}</div>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={handleAddMethod}
                      disabled={saving}
                      className="flex-1 py-3 px-6 bg-secondary text-white rounded-lg hover:bg-secondary/90 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? 'Adding...' : 'Add Payment Method'}
                    </button>
                    <button
                      onClick={() => {
                        setShowAddModal(false);
                        setSelectedType(null);
                        setError('');
                      }}
                      disabled={saving}
                      className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


